import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface EmployerSignatureProps {
  date: string;
  onDateChange: (date: string) => void;
  signatureDataUrl: string | null;
  onSignatureChange: (dataUrl: string | null) => void;
  nameClarification: string;
  onNameClarificationChange: (value: string) => void;
  onSignatureModeChange?: (isDigitally: boolean) => void;
  uploadedFileName?: string;
  onUploadedFileNameChange?: (name: string) => void;
  errors?: { date?: string; signature?: string; name?: string };
}

export const EmployerSignature: React.FC<EmployerSignatureProps> = ({
  date,
  onDateChange,
  signatureDataUrl,
  onSignatureChange,
  nameClarification,
  onNameClarificationChange,
  onSignatureModeChange,
  onUploadedFileNameChange,
  errors,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [drawMode, setDrawMode] = useState(false);

  // Sync canvas internal pixel size to its CSS display size, accounting for DPR
  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== Math.round(rect.width * dpr) || canvas.height !== Math.round(rect.height * dpr)) {
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
    }
  }, []);

  // Sync size on mount and on window resize
  useEffect(() => {
    syncCanvasSize();
    const observer = new ResizeObserver(() => syncCanvasSize());
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [syncCanvasSize]);

  // Get accurate canvas-relative position accounting for DPR
  const getPos = (e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  useEffect(() => {
    if (!drawMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    syncCanvasSize();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const startDrawing = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      isDrawingRef.current = true;
      const pos = getPos(e, canvas);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const draw = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!isDrawingRef.current) return;
      const pos = getPos(e, canvas);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const stopDrawing = () => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      ctx.beginPath();
      onSignatureChange(canvas.toDataURL());
    };

    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseleave', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
    };
  }, [drawMode, onSignatureChange, syncCanvasSize]);

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onSignatureChange(null);
  }, [onSignatureChange]);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      onSignatureChange(dataUrl);
      onSignatureModeChange?.(true); // uploaded = isDigitally true
      onUploadedFileNameChange?.(file.name);
      setDrawMode(false);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleDraw = () => {
    setDrawMode(true);
    onSignatureModeChange?.(false); // drawn = isDigitally false
    onUploadedFileNameChange?.('');
    if (!signatureDataUrl) return;
    handleClear();
  };

  return (
    <div className="ec-signature-panel">
      <h4 className="ec-signature-panel-title">
        {t('ON_BEHALF_OF_EMPLOYER') || 'On behalf of the employer:'}
      </h4>

      <div className="ec-field-group">
        <label className="ec-field-label">{t('DATE') || 'Date'}</label>
        <input
          className="ec-field-input"
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          placeholder="dd/mm/yyyy"
        />
        {errors?.date && <p className="ec-validation-error">{t(errors.date)}</p>}
      </div>

      <div className="ec-field-group">
        <label className="ec-field-label">{t('SIGNATURE') || 'Signature'}</label>
        <div className={`ec-signature-canvas-wrapper${drawMode ? ' ec-signature-canvas-wrapper--active' : ''}`}>
          <canvas
            ref={canvasRef}
            className="ec-signature-canvas"
          />
          {!drawMode && !signatureDataUrl && (
            <span className="ec-signature-canvas-placeholder">
              {t('CLICK_DRAW_TO_SIGN') || 'Click "Draw Signature" to sign here'}
            </span>
          )}
        </div>
        <div className="ec-signature-actions">
          <button type="button" className="ec-signature-btn ec-signature-btn--draw" onClick={handleDraw}>
            {t('DRAW_SIGNATURE') || 'Draw Signature'}
          </button>
          <button type="button" className="ec-signature-btn ec-signature-btn--upload" onClick={() => fileInputRef.current?.click()}>
            {t('UPLOAD_SIGNATURE') || 'Upload Signature'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
        </div>
        {(signatureDataUrl || drawMode) && (
          <button type="button" className="ec-signature-clear-btn" onClick={handleClear}>
            {t('CLEAR') || 'Clear'}
          </button>
        )}
        {errors?.signature && <p className="ec-validation-error">{t(errors.signature)}</p>}
      </div>

      <div className="ec-field-group">
        <label className="ec-field-label">{t('NAME_CLARIFICATION') || 'Name Clarification'}</label>
        <input
          className="ec-field-input"
          type="text"
          value={nameClarification}
          onChange={(e) => onNameClarificationChange(e.target.value)}
          placeholder={t('ENTER_NAME_CLARIFICATION') || 'Enter name clarification...'}
        />
        {errors?.name && <p className="ec-validation-error">{t(errors.name)}</p>}
      </div>
    </div>
  );
};
